import { featureSoundUrls } from '@services/interactions-service';
import useSound from 'use-sound';
import SettingBlock from '@components/Presentation/main/Settings/units/SettingBlock';
import SettingSwitch from '@molecules/SettingSwitch';

import Button from '@atoms/new/button';
import { useEffect, useState } from 'react';
import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@atoms/new/dropdown';
import Checkbox from '@atoms/new/checkbox';
import { CaretDownIcon, PauseIcon, PlayIcon as PlayArrowRoundedIcon } from '@phosphor-icons/react';

interface SettingsUIProps {
  className?: string;
  showLabel?: boolean;
  showBorder?: boolean;
  showEnabledSwitch?: boolean;
  checkBox?: boolean;
  showPadding?: boolean;
}
interface SoundSettingsProps {
  featureType: string;
  setting: any;
  uiOptions?: SettingsUIProps;
  updateSetting: (key: string, value: any) => void;
  onChangeHandler?: (e: any) => void;
  isDisabled?: boolean;
}
interface soundOptionInterface {
  name: string;
  value: string;
  url: string;
  isDefault: boolean;
  volume?: number;
}
interface configInterface {
  soundOptions: soundOptionInterface[];
  selectedSoundKey: string;
  isEnabledKey: string;
  soundOnActionText: string | null;
  soundKeys: Record<string, string>;
  label: string;
  description: string;
  showHideFeature: boolean;
}

type featureSoundConfigInterface = Record<string, configInterface>;

export const featureSoundConfig: featureSoundConfigInterface = {
  map: {
    soundOptions: [
      { name: 'Pop', value: 'pop', url: featureSoundUrls.map.pop, isDefault: true, volume: 0.5 },
      { name: 'Chime', value: 'chime', url: featureSoundUrls.map.chime, isDefault: false, volume: 0.5 }
    ],
    selectedSoundKey: 'mapSoundOptions',
    isEnabledKey: 'mapSound',
    soundOnActionText: 'Location',
    soundKeys: {
      chime: 'chime',
      pop: 'pop'
    },
    label: 'Magic Maps Sound',
    description: 'Magic Maps sound &nbsp;',
    showHideFeature: true
  },
  talkingTiles: {
    soundOptions: [
      { name: 'Soft Thud', value: 'soft_thud', url: featureSoundUrls.talkingTiles.soft_thud, isDefault: true, volume: 0.5 },
      { name: 'Arcade', value: 'arcade', url: featureSoundUrls.talkingTiles.arcade, isDefault: false, volume: 0.5 },
      { name: 'Whoosh', value: 'whoosh', url: featureSoundUrls.talkingTiles.whoosh, isDefault: false, volume: 1 },
      { name: 'Brick Fall', value: 'brick_fall', url: featureSoundUrls.talkingTiles.brick_fall, isDefault: false, volume: 0.5 }
    ],
    selectedSoundKey: 'talkingTilesSoundOptions',
    isEnabledKey: 'isTalkingTilesSoundEnabled',
    soundOnActionText: 'Tile disappearance',
    soundKeys: {
      whoosh: 'whoosh',
      brick_fall: 'brick_fall'
    },
    label: 'Talking Tile Sound',
    description: 'Talking Tile sound &nbsp;',
    showHideFeature: true
  },
  // quiz: {
  //   soundOptions: [
  //     { name: 'A Christmas Dance', value: 'a_christmas_dance', url: featureSoundUrls.quiz.a_christmas_dance, isDefault: true, volume: 1 },
  //     { name: 'Unfinished stories', value: 'unfinished_stories', url: featureSoundUrls.quiz.unfinished_stories, isDefault: false, volume: 0.5 }
  //   ],
  //   selectedSoundKey: 'quizSoundOptions',
  //   isEnabledKey: 'isQuizSoundEnabled',
  //   soundOnActionText: 'On load',
  //   soundKeys: {
  //     a_christmas_dance: 'a_christmas_dance',
  //     unfinished_stories: 'unfinished_stories'
  //   },
  //   label: 'Quiz Sound',
  //   description: 'Quiz sound &nbsp;',
  //   showHideFeature: true
  // },
  wheelSpin: {
    soundOptions: [
      { name: 'Tick', value: 'tick', url: featureSoundUrls.wheelSpin.tick, isDefault: true, volume: 0.5 },
      { name: 'Glass', value: 'glass', url: featureSoundUrls.wheelSpin.glass, isDefault: false, volume: 0.5 },
    ],
    selectedSoundKey: 'wheelSpinSoundOptions',
    isEnabledKey: 'isWheelSpinSoundEnabled',
    soundOnActionText: 'Spinning',
    soundKeys: {
      glass: 'glass',
      tick: 'tick'
    },
    label: 'Wheel Spin Sound',
    description: 'Wheel Spin sound &nbsp;',
    showHideFeature: true
  },
  wheelButton: {
    soundOptions: [
      { name: 'Button', value: 'button', url: featureSoundUrls.wheelButton.button, isDefault: true, volume: 0.5 },
      { name: 'Dial Button', value: 'knob_button', url: featureSoundUrls.wheelButton.knob_button, isDefault: false, volume: 0.5 },
    ],
    selectedSoundKey: 'wheelButtonSoundOptions',
    isEnabledKey: 'isWheelButtonSoundEnabled',
    soundOnActionText: 'Start',
    soundKeys: {
      button: 'button',
      knob_button: 'knob_button'
    },
    label: 'Wheel Button Sound',
    description: 'Wheel Button sound &nbsp;',
    showHideFeature: true
  },
  wheelResult: {
    soundOptions: [
      { name: 'Fanfare', value: 'fanfare', url: featureSoundUrls.wheelResult.fanfare, isDefault: true, volume: 0.5 },
      { name: 'Applause', value: 'applause', url: featureSoundUrls.wheelResult.applause, isDefault: false, volume: 0.5 },
    ],
    selectedSoundKey: 'wheelResultSoundOptions',
    isEnabledKey: 'isWheelResultSoundEnabled',
    soundOnActionText: 'Ending',
    soundKeys: {
      fanfare: 'fanfare',
      applause: 'applause'
    },
    label: 'Wheel Result Sound',
    description: 'Wheel Result sound &nbsp;',
    showHideFeature: true
  },
  credits: {
    soundOptions: [
      { name: 'Stellar Voyage', value: 'stellar_voyage', url: featureSoundUrls.credits.stellar_voyage, isDefault: true, volume: 0.4 },
    ],
    selectedSoundKey: 'creditsSoundOptions',
    isEnabledKey: 'isCreditsSoundEnabled',
    soundOnActionText: null,
    soundKeys: {
      stellar_voyage: 'stellar_voyage'
    },
    label: 'Credits Sound',
    description: 'Credits sound &nbsp;',
    showHideFeature: true
  }

};

const defaultUiOptions: SettingsUIProps = {
  className: '',
  showLabel: true,
  showBorder: true,
  showEnabledSwitch: true,
  checkBox: false,
  showPadding: true
};

const SoundSettings = ({ featureType, setting, updateSetting, uiOptions = defaultUiOptions, onChangeHandler, isDisabled }: SoundSettingsProps) => {
  const config = featureSoundConfig[featureType];

  const selectedSound = config?.soundOptions.find(option => option.value === setting[config.selectedSoundKey]) || config?.soundOptions.find(option => option.isDefault);
  const defaultSound = config?.soundOptions.find(option => option.isDefault);
  const sound = selectedSound?.url || defaultSound?.url || '';
  const volume = selectedSound?.volume || defaultSound?.volume || 0.5;
  const [isPlaying, setIsPlaying] = useState(false);
  const [play, { stop, sound: soundPlayer }] = useSound(sound, {
    volume,
    onend: () => setIsPlaying(false)
  });

  useEffect(() => {
    return () => {
      soundPlayer?.stop?.();
    };
  }, [soundPlayer]);

  const togglePlayback = () => {
    if (!setting[config.isEnabledKey]) return;
    if (isPlaying) {
      stop();
      setIsPlaying(false);
    } else {
      play();
      setIsPlaying(true);
    }
  };

  if (!config) return null;

  if (!uiOptions.showLabel) {
    return (
      <div className='flex flex-col gap-2'>
        <div className="self-stretch inline-flex justify-start items-center gap-2.5">
          <div
            onClick={() => {
              if (uiOptions.checkBox && onChangeHandler) {
                if (isDisabled) return;
                onChangeHandler({ target: { checked: !setting[config.isEnabledKey] }, key: config.isEnabledKey });
              }
            }}
            className={`${(!setting[config.isEnabledKey] && !uiOptions.checkBox) && 'opacity-50'}  justify-start text-default-900 text-sm font-normal leading-tight flex items-center cursor-pointer`}>
            {uiOptions.checkBox && (
              <Checkbox
                isSelected={setting[config.isEnabledKey]}
                isDisabled={isDisabled}
                onChange={(e: any) => {
                  updateSetting(config.isEnabledKey, e.target.checked);
                }}
              />
            )}
            {config.soundOnActionText && `${config.soundOnActionText}:`}
          </div>
        </div>
        <div className='flex items-center justify-center gap-2'>
          <Dropdown
            isDisabled={!setting[config.isEnabledKey]}
            className="w-full z-1"
          >
            <DropdownTrigger>
              <Button
                kind="tertiary"
                fullWidth
                className="flex items-center gap-3"
              >
                <p className='font-normal'>
                  {config.soundOptions.find(
                    (option) =>
                      option.value === setting[config.selectedSoundKey]
                  )?.name || defaultSound?.name}
                </p>
                <div className="ml-auto">
                  <CaretDownIcon size={16} />
                </div>
              </Button>
            </DropdownTrigger>
            <DropdownMenu className="w-[260px]">

              {config.soundOptions.map((option) => (
                <DropdownItem
                  onClick={() => {
                    stop();
                    setIsPlaying(false);
                    updateSetting(config.selectedSoundKey, option.value);
                  }}
                  key={option.value}
                  value={option.value}
                >
                  {option.name}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <Button
            kind='secondary'
            isIconOnly={true}
            isDisabled={!setting[config.isEnabledKey]}
            onPress={togglePlayback}
            size='md'>
            {isPlaying ? <PauseIcon size={20} weight="fill" /> : <PlayArrowRoundedIcon size={20} />}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SettingBlock
      title="Sound"
      titleMargin="sm"
      hasBorderTop={uiOptions.showBorder}
      addPadding={uiOptions.showPadding}
      className={uiOptions.className}
    >
      <SettingSwitch
        label={config.label}
        settingKey={config.isEnabledKey}
        settingValue={setting[config.isEnabledKey]}
        onChangeHandler={(key: string, value: boolean) => {
          stop();
          setIsPlaying(false);
          updateSetting(key, value);
        }}
        tooltipContent={''}
      />
      <div className='flex flex-col gap-2'>
        <div className="self-stretch inline-flex justify-start items-center gap-2.5">
          <div
            onClick={() => {
              if (uiOptions.checkBox && onChangeHandler) {
                if (isDisabled) return;
                onChangeHandler({ target: { checked: !setting[config.isEnabledKey] }, key: config.isEnabledKey });
              }
            }}
            className={`${(!setting[config.isEnabledKey] && !uiOptions.checkBox) && 'opacity-50'}  justify-start text-default-900 text-sm font-normal leading-tight flex items-center cursor-pointer`}>
            {uiOptions.checkBox &&
              <Checkbox
                isSelected={setting[config.isEnabledKey]}
                isDisabled={isDisabled}
                onChange={(e: any) => {
                  updateSetting(config.isEnabledKey, e.target.checked);
                }}
              />}
            {config.soundOnActionText && `${config.soundOnActionText}:`}
          </div>
        </div>
        <div className='flex items-center justify-center gap-2'>
          <Dropdown
            isDisabled={!setting[config.isEnabledKey]}
            className="w-full z-1"
          >
            <DropdownTrigger>
              <Button
                kind="tertiary"
                fullWidth
                className="flex items-center gap-3"
              >
                <p className='font-normal'>
                  {config.soundOptions.find(
                    (option) =>
                      option.value === setting[config.selectedSoundKey]
                  )?.name || defaultSound?.name}
                </p>
                <div className="ml-auto">
                  <CaretDownIcon size={16} />
                </div>
              </Button>
            </DropdownTrigger>
            <DropdownMenu className="w-[260px]">

              {config.soundOptions.map((option) => (
                <DropdownItem
                  onClick={() => {
                    stop();
                    setIsPlaying(false);
                    updateSetting(config.selectedSoundKey, option.value);
                  }}
                  key={option.value}
                  value={option.value}
                >
                  {option.name}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
          <Button
            kind='secondary'
            isIconOnly={true}
            isDisabled={!setting[config.isEnabledKey]}
            onPress={togglePlayback}
            size='md'>
            {isPlaying ? <PauseIcon size={20} weight="fill" /> : <PlayArrowRoundedIcon size={20} />}
          </Button>
        </div>
      </div>
    </SettingBlock>
  );
};

export default SoundSettings;
